import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import EnrollPopup from '../components/EnrollPopup';
import './CourseDetails.css';

const CourseDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showEnrollPopup, setShowEnrollPopup] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'chapters' | 'reviews'>('overview');

  // Mock data - will be replaced with API call
  const course = {
    id: 1,
    title: 'Advanced Mathematics',
    instructor: 'Dr. Smith',
    category: 'Mathematics',
    price: 49,
    originalPrice: 99,
    rating: 4.8,
    reviewsCount: 245,
    studentsCount: 1200,
    duration: '12 hours',
    level: 'Intermediate',
    language: 'English',
    description: 'Learn advanced mathematical concepts including calculus, algebra, and geometry. This comprehensive course covers all essential topics for students at intermediate level.',
    image: '📚',
    isEnrolled: false,
    isFree: false,
    chapters: [
      { id: 1, title: 'Introduction to Calculus', isFree: true, videosCount: 5, isCompleted: true, progress: 100 },
      { id: 2, title: 'Differential Equations', isFree: false, videosCount: 8, isCompleted: false, progress: 50 },
      { id: 3, title: 'Integration Techniques', isFree: false, videosCount: 6, isCompleted: false, progress: 0 },
      { id: 4, title: 'Advanced Applications', isFree: false, videosCount: 10, isCompleted: false, progress: 0 }
    ],
    reviews: [
      { id: 1, user: 'Ahmed', rating: 5, comment: 'Excellent course! Very well explained.', date: '2024-01-15' },
      { id: 2, user: 'Sara', rating: 4, comment: 'Good content but could use more examples.', date: '2024-01-10' }
    ]
  };

  const handleEnroll = () => {
    if (!course.isEnrolled) {
      setShowEnrollPopup(true);
    } else {
      navigate(`/course/${id}/chapters`);
    }
  };

  return (
    <div className="course-details-page">
      <header className="course-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>Course Details</h1>
        <button className="share-button">📤</button>
      </header>

      <div className="course-details-content">
        {/* Course Image/Header */}
        <div className="course-image-section">
          <div className="course-image-large">{course.image}</div>
          {course.isFree && <span className="free-badge">FREE</span>}
          {course.originalPrice > course.price && (
            <span className="discount-badge">
              {Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100)}% OFF
            </span>
          )}
        </div>

        {/* Course Info */}
        <div className="course-info-section">
          <div className="course-header-info">
            <span className="course-category">{course.category}</span>
            <div className="course-rating">
              <span className="stars">⭐ {course.rating}</span>
              <span className="reviews-count">({course.reviewsCount})</span>
            </div>
          </div>
          <h2 className="course-title-large">{course.title}</h2>
          <p className="course-instructor-large">By {course.instructor}</p>
          
          <div className="course-meta-info">
            <div className="meta-item">
              <span className="meta-icon">👥</span>
              <span>{course.studentsCount} students</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">⏱️</span>
              <span>{course.duration}</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">📊</span>
              <span>{course.level}</span>
            </div>
          </div>

          {/* Price */}
          <div className="course-price-section">
            {course.isFree ? (
              <span className="price-free">Free</span>
            ) : (
              <>
                <span className="price-current">${course.price}</span>
                {course.originalPrice > course.price && (
                  <span className="price-original">${course.originalPrice}</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="course-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'chapters' ? 'active' : ''}`}
            onClick={() => setActiveTab('chapters')}
          >
            Chapters ({course.chapters.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews ({course.reviewsCount})
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <h3>About this course</h3>
              <p className="course-description">{course.description}</p>
              
              <h3>What you'll learn</h3>
              <ul className="learning-objectives">
                <li>✓ Master advanced calculus concepts</li>
                <li>✓ Solve complex differential equations</li>
                <li>✓ Apply integration techniques</li>
                <li>✓ Understand mathematical proofs</li>
              </ul>
            </div>
          )}

          {activeTab === 'chapters' && (
            <div className="chapters-content-tab">
              {course.chapters.map((chapter, index) => {
                const isLocked = !chapter.isFree && chapter.progress === 0 && !chapter.isCompleted;
                
                return (
                  <div
                    key={chapter.id}
                    className={`chapter-card-main ${isLocked ? 'locked' : ''} ${chapter.isCompleted ? 'completed' : ''}`}
                    onClick={() => {
                      if (!isLocked) {
                        navigate(`/course/${id}/chapter/${chapter.id}`);
                      }
                    }}
                  >
                    <div className="chapter-left-main">
                      <div className={`chapter-number-main ${chapter.isCompleted ? 'completed' : ''}`}>
                        {chapter.isCompleted ? '✓' : index + 1}
                      </div>
                      <div className="chapter-info-main">
                        <h3 className="chapter-title-main">{chapter.title}</h3>
                        <div className="chapter-details-main">
                          <span className="chapter-videos-main">📹 {chapter.videosCount} videos</span>
                          {chapter.isFree && <span className="free-badge-main">Free</span>}
                        </div>
                        {chapter.progress > 0 && !chapter.isCompleted && (
                          <div className="chapter-progress-main">
                            <div className="progress-bar-main">
                              <div 
                                className="progress-fill-main"
                                style={{ width: `${chapter.progress}%` }}
                              ></div>
                            </div>
                            <span className="progress-text-main">{chapter.progress}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="chapter-right-main">
                      {isLocked ? (
                        <span className="lock-icon-main">🔒</span>
                      ) : chapter.isCompleted ? (
                        <span className="check-icon-main">✓</span>
                      ) : (
                        <button className="play-icon-main">▶</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="reviews-content">
              <div className="reviews-summary">
                <div className="rating-breakdown">
                  <div className="rating-average">
                    <span className="rating-number">{course.rating}</span>
                    <span className="rating-stars">⭐⭐⭐⭐⭐</span>
                    <span className="rating-total">Based on {course.reviewsCount} reviews</span>
                  </div>
                </div>
              </div>
              {course.reviews.map((review) => (
                <div key={review.id} className="review-item">
                  <div className="review-header">
                    <div className="review-user">
                      <div className="user-avatar-small">{review.user[0]}</div>
                      <span className="user-name">{review.user}</span>
                    </div>
                    <div className="review-rating">⭐ {review.rating}</div>
                  </div>
                  <p className="review-comment">{review.comment}</p>
                  <span className="review-date">{review.date}</span>
                </div>
              ))}
              <button className="view-all-reviews-button">View All Reviews</button>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="course-action-bar">
        {course.isEnrolled ? (
          <button className="continue-button" onClick={() => navigate(`/course/${id}/chapters`)}>
            Continue Learning
          </button>
        ) : (
          <button className="enroll-button-large" onClick={handleEnroll}>
            {course.isFree ? 'Enroll for Free' : `Enroll Now - $${course.price}`}
          </button>
        )}
      </div>

      <EnrollPopup 
        isOpen={showEnrollPopup} 
        onClose={() => setShowEnrollPopup(false)} 
      />
      <BottomNavigation />
    </div>
  );
};

export default CourseDetails;
